import { useCallback, useEffect, useState } from 'react';

import type { OtpContext } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import {
  loginWithOtp,
  OtpError,
  OtpErrorType,
  sendOtpCode,
  verifyOtpCode,
} from '../../../utils/authApi';
import type { AnalyticsHandler } from './useSignupFlow';

const RESEND_COOLDOWN_SECONDS = 60;

type UseCodeStepOptions = {
  apiBaseUrl: string;
  email: string;
  context?: OtpContext;
  onVerified: (idpToken: string, tokenExpiresAt: number, isExistingUser: boolean) => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseCodeStepReturn = {
  code: string;
  setCode: (value: string) => void;
  codeError?: string;
  verifyingCode: boolean;
  sendingCode: boolean;
  resendCooldown: number;
  handleVerifyCode: () => Promise<void>;
  handleResendCode: () => Promise<void>;
};

export const useCodeStep = ({
  apiBaseUrl,
  email,
  context,
  onVerified,
  onAnalyticsEvent,
}: UseCodeStepOptions): UseCodeStepReturn => {
  const [code, setCodeValue] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resend cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const setCode = useCallback(
    (value: string) => {
      setCodeValue(value);
      if (codeError) {
        setCodeError(undefined);
      }
    },
    [codeError]
  );

  const handleVerifyCode = useCallback(async () => {
    if (code.length < 8) {
      setCodeError('Please enter the 8-character code');
      return;
    }

    setVerifyingCode(true);
    setCodeError(undefined);

    try {
      const response = await verifyOtpCode(apiBaseUrl, email, code);

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_CODE_VERIFIED, {
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      const tokenExpiresAt = Date.now() + 5 * 60 * 1000;
      const loginResponse = await loginWithOtp(apiBaseUrl, response.idpToken);

      const isExistingUser = !loginResponse.signupRequired;
      onVerified(response.idpToken, tokenExpiresAt, isExistingUser);
    } catch (error) {
      const otpError = error instanceof OtpError ? error : null;
      const errorMessage = error instanceof Error ? error.message : 'Invalid code';

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_ERROR, {
        [ANALYTICS_KEYS.ERROR_TYPE]: otpError?.type || 'unknown',
        [ANALYTICS_KEYS.ERROR_MESSAGE]: errorMessage,
        [ANALYTICS_KEYS.STEP]: 'verify_code',
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      switch (otpError?.type) {
        case OtpErrorType.EXPIRED_CODE:
          setCodeError('This code has expired. Please request a new one.');
          break;
        case OtpErrorType.MAX_ATTEMPTS:
          setCodeError('Too many incorrect attempts. Please request a new code.');
          break;
        case OtpErrorType.INVALID_CODE:
          setCodeError('Invalid code. Please check and try again.');
          break;
        case OtpErrorType.NETWORK_ERROR:
          setCodeError('Verification failed. Please try again.');
          break;
        default:
          setCodeError('Verification failed. Please try again.');
      }
    } finally {
      setVerifyingCode(false);
    }
  }, [apiBaseUrl, code, context, email, onAnalyticsEvent, onVerified]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) {
      return;
    }

    setSendingCode(true);
    setCodeError(undefined);

    try {
      await sendOtpCode(apiBaseUrl, email, context);

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_CODE_RESENT, {
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      setCodeError(undefined);
    } catch (error) {
      const otpError = error instanceof OtpError ? error : null;
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend code';

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_ERROR, {
        [ANALYTICS_KEYS.ERROR_TYPE]: otpError?.type || 'unknown',
        [ANALYTICS_KEYS.ERROR_MESSAGE]: errorMessage,
        [ANALYTICS_KEYS.STEP]: 'resend_code',
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      if (otpError?.type === OtpErrorType.RATE_LIMIT) {
        const cooldown = otpError.cooldownSeconds ?? RESEND_COOLDOWN_SECONDS;
        setResendCooldown(cooldown);
        setCodeError(errorMessage);
      } else {
        setCodeError(errorMessage);
      }
    } finally {
      setSendingCode(false);
    }
  }, [apiBaseUrl, context, email, onAnalyticsEvent, resendCooldown]);

  return {
    code,
    setCode,
    codeError,
    verifyingCode,
    sendingCode,
    resendCooldown,
    handleVerifyCode,
    handleResendCode,
  };
};
